/**
 * Preislisten-Detail — Positionen inline editierbar (Preis, enthaltene
 * Menge, Mindestmenge, Beschreibung), gruppiert nach Seite (Erlös/Kosten)
 * und Kategorie. Speichern ersetzt alle Positionen (PUT).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PriceListDetail, PriceListItem, activatePriceList, extractApiError, getPriceList, updatePriceList } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { BillingTabs } from '../components/billing/BillingTabs';

const CATEGORY_LABEL: Record<string, string> = {
  plan_base: 'Grundgebühren', seat: 'Nutzer / Seats', module: 'Module', usage: 'Nutzungsbasiert', addon: 'Add-ons', infra: 'Infrastruktur-Selbstkosten',
};
const CATEGORY_ORDER = ['plan_base', 'seat', 'module', 'usage', 'addon', 'infra'];

type Draft = PriceListItem & { _price: string; _included: string; _min: string };

function toDraft(i: PriceListItem): Draft {
  return { ...i, _price: (i.unitPriceCents / 100).toFixed(2), _included: i.includedQuantity === null ? '' : String(i.includedQuantity), _min: i.minQuantity === null ? '' : String(i.minQuantity) };
}

export function PriceListDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { operator } = useAuth();
  const isReadOnly = !!operator?.readOnly;
  const [list, setList] = useState<PriceListDetail | null>(null);
  const [items, setItems] = useState<Draft[]>([]);
  const [name, setName] = useState('');
  const [tax, setTax] = useState('19');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function apply(pl: PriceListDetail): void {
    setList(pl); setItems(pl.items.map(toDraft)); setName(pl.name); setTax((pl.taxRateBp / 100).toString()); setDirty(false);
  }
  useEffect(() => { if (id) getPriceList(id).then(apply).catch((e) => setError(extractApiError(e))); }, [id]);

  const editable = !isReadOnly && list?.status !== 'archived';

  function patch(code: string, field: '_price' | '_included' | '_min' | 'description', value: string): void {
    setItems((prev) => prev.map((i) => (i.code === code ? { ...i, [field]: value } : i)));
    setDirty(true);
  }

  async function save(): Promise<void> {
    if (!id) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const payload = items.map((i) => {
        const cents = Math.round(Number(i._price.replace(',', '.')) * 100);
        if (!Number.isFinite(cents)) throw new Error(`Ungültiger Preis bei ${i.code}`);
        const inc = i._included.trim() === '' ? null : Number(i._included.replace(',', '.'));
        const min = i._min.trim() === '' ? null : Number(i._min.replace(',', '.'));
        return {
          code: i.code, side: i.side, category: i.category, plan: i.plan, metric: i.metric, unit: i.unit, unitPriceCents: cents,
          includedQuantity: inc, minQuantity: min, description: i.description, sortOrder: i.sortOrder,
        };
      });
      const taxBp = Math.round(Number(tax.replace(',', '.')) * 100);
      apply(await updatePriceList(id, { name, taxRateBp: taxBp, items: payload }));
      setNotice('Gespeichert.');
    } catch (e) { setError(extractApiError(e)); } finally { setBusy(false); }
  }

  const groups = useMemo(() => {
    const out: Array<{ side: string; category: string; items: Draft[] }> = [];
    for (const side of ['revenue', 'cost']) {
      for (const cat of CATEGORY_ORDER) {
        const its = items.filter((i) => i.side === side && i.category === cat);
        if (its.length) out.push({ side, category: cat, items: its });
      }
    }
    return out;
  }, [items]);

  if (!list) return <div><BillingTabs />{error ? <p className="text-red-700">{error}</p> : <p className="text-slate-500">Lade…</p>}</div>;

  return (
    <div>
      <BillingTabs />
      <Link to="/billing/price-lists" className="text-sm text-primary-600 hover:underline">← Preislisten</Link>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-1 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <input value={name} onChange={(e) => { setName(e.target.value); setDirty(true); }} disabled={!editable} className="text-xl font-semibold px-2 py-1 border rounded" />
          <span className="badge bg-slate-200 font-mono">{list.brand}</span>
          <span className={`badge ${list.status === 'active' ? 'bg-green-100 text-green-800' : list.status === 'draft' ? 'bg-blue-100 text-blue-800' : 'bg-slate-200'}`}>{list.status}</span>
          <label className="text-sm text-slate-600 flex items-center gap-1">MwSt <input value={tax} onChange={(e) => { setTax(e.target.value); setDirty(true); }} disabled={!editable} className="w-16 px-2 py-1 border rounded font-mono" /> %</label>
        </div>
        <div className="flex gap-2">
          {editable && <button type="button" className="btn-primary" disabled={busy || !dirty} onClick={() => void save()}>{busy ? 'Speichere…' : 'Speichern'}</button>}
          {editable && list.status === 'draft' && (
            <button type="button" className="btn-secondary" disabled={busy || dirty} title={dirty ? 'Erst speichern' : undefined} onClick={() => { if (window.confirm(`Liste aktivieren? Die bisher aktive Liste der Marke ${list.brand} wird archiviert.`)) { setBusy(true); activatePriceList(list.id).then(apply).catch((e) => setError(extractApiError(e))).finally(() => setBusy(false)); } }}>
              Aktivieren
            </button>
          )}
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">{error}</div>}
      {notice && <div className="bg-green-50 border border-green-200 text-green-800 rounded p-3 mb-4 text-sm">{notice}</div>}
      {list.status === 'archived' && <p className="text-sm text-slate-500 mb-3">Archivierte Listen sind schreibgeschützt (Abrechnungen referenzieren sie).</p>}
      <p className="text-xs text-slate-500 mb-3">
        <strong>Enthalten</strong>: im Plan enthaltene Menge (leer = Plan-Quota; bei Kosten-Footprints die Menge selbst). <strong>Mindestmenge</strong>: Mindestabnahme (Seats).
        Faktoren (Packung, Backup) werden als Preis × 100 gepflegt (150 = 1,5×).
      </p>

      {groups.map((g) => (
        <div key={`${g.side}-${g.category}`} className="bg-white rounded shadow mb-4">
          <div className="px-3 py-2 border-b flex items-center gap-2">
            <span className={`badge ${g.side === 'revenue' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{g.side === 'revenue' ? 'Erlös' : 'Kosten'}</span>
            <h3 className="font-semibold">{CATEGORY_LABEL[g.category] ?? g.category}</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr><th className="p-2">Code</th><th className="p-2">Plan</th><th className="p-2">Einheit</th><th className="p-2 text-right">Preis (€)</th><th className="p-2 text-right">Enthalten</th><th className="p-2 text-right">Mindestmenge</th><th className="p-2">Beschreibung</th></tr>
            </thead>
            <tbody>
              {g.items.map((i) => (
                <tr key={i.code} className="border-t">
                  <td className="p-2 font-mono text-xs">{i.code}{i.metric && <span className="block text-slate-400">{i.metric}</span>}</td>
                  <td className="p-2">{i.plan ?? <span className="text-slate-300">—</span>}</td>
                  <td className="p-2 font-mono text-xs">{i.unit}</td>
                  <td className="p-2 text-right"><input value={i._price} disabled={!editable} onChange={(e) => patch(i.code, '_price', e.target.value)} className="w-24 px-2 py-1 border rounded text-right font-mono" /></td>
                  <td className="p-2 text-right"><input value={i._included} disabled={!editable} onChange={(e) => patch(i.code, '_included', e.target.value)} placeholder="Quota" className="w-24 px-2 py-1 border rounded text-right font-mono" /></td>
                  <td className="p-2 text-right"><input value={i._min} disabled={!editable} onChange={(e) => patch(i.code, '_min', e.target.value)} placeholder="—" className="w-20 px-2 py-1 border rounded text-right font-mono" /></td>
                  <td className="p-2"><input value={i.description ?? ''} disabled={!editable} onChange={(e) => patch(i.code, 'description', e.target.value)} className="w-full px-2 py-1 border rounded text-xs" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
